/**
 * Passcode entry view (Req 1.2, 1.3, 1.5, 1.6, 1.7).
 *
 * A single Passcode field (no username — Req 1.1) that posts to
 * `POST /api/auth/login`. On success it invokes `onAuthenticated` so the app
 * enters the authenticated shell. A wrong Passcode shows a single generic
 * failure message (Req 1.3); a lockout (HTTP 423) shows the "temporarily
 * locked" message (Req 1.5). This is the interface shown whenever there is no
 * active Session (Req 1.6).
 */
import type { ApiClient } from "../api/client";
import { el, render } from "../ui/dom";

/** Build the Passcode entry view. `onAuthenticated` runs after a successful login. */
export function passcodeView(
  api: ApiClient,
  onAuthenticated: () => void
): HTMLElement {
  const message = el("p", {
    class: "form-message",
    attrs: { role: "alert", "aria-live": "assertive" },
  });

  const input = el("input", {
    type: "password",
    name: "passcode",
    placeholder: "Passcode",
    class: "field-input",
    attrs: {
      "aria-label": "Passcode",
      autocomplete: "current-password",
      inputmode: "text",
      autocapitalize: "off",
      autocorrect: "off",
      required: "true",
    },
  });

  const submit = el("button", {
    type: "submit",
    class: "btn btn-primary",
    text: "Enter",
  });

  let submitting = false;

  const setMessage = (text: string, kind: "error" | "info"): void => {
    message.textContent = text;
    message.className = `form-message ${kind === "error" ? "is-error" : "is-info"}`;
  };

  const form = el(
    "form",
    {
      class: "passcode-form",
      on: {
        submit: (event: Event) => {
          event.preventDefault();
          void attempt();
        },
      },
    },
    [
      el("label", { class: "field-label", text: "Passcode", attrs: { for: "passcode" } }),
      input,
      submit,
      message,
    ]
  );

  async function attempt(): Promise<void> {
    if (submitting) {
      return;
    }
    const passcode = input.value;
    if (passcode.length === 0) {
      setMessage("Enter the shared passcode.", "error");
      return;
    }

    submitting = true;
    submit.setAttribute("disabled", "");
    setMessage("Checking…", "info");

    const result = await api.login(passcode);

    submitting = false;
    submit.removeAttribute("disabled");

    if (result.ok) {
      input.value = "";
      onAuthenticated();
      return;
    }

    if (result.status === 423) {
      // Global brute-force lockout in effect (Req 1.5).
      setMessage(
        "Passcode entry is temporarily locked. Please try again later.",
        "error"
      );
      return;
    }

    // Any other failure (401 wrong passcode, network) is a single generic
    // message with no enumeration (Req 1.3).
    setMessage("Authentication failed. Check the passcode and try again.", "error");
  }

  const container = el("section", { class: "view passcode-view" }, [
    el("h1", { class: "app-title", text: "Grain Breakeven Tracker" }),
    el("p", {
      class: "passcode-lead",
      text: "Enter the shared passcode to view corn and soybean breakevens.",
    }),
    form,
  ]);

  // Focus the field for a fast mobile entry.
  queueMicrotask(() => input.focus());

  // Return a wrapper so callers can mount consistently.
  const root = el("div", { class: "screen screen-centered" });
  render(root, container);
  return root;
}
